import { useCallback } from "react";

import { deleteItemAsync, getItemAsync, setItemAsync } from "expo-secure-store";

import useAppMonitor from "@/services/AppMonitor";

enum SecureStorageKey {
  Email = "email",
  Password = "password",
}

// SecureStorage is meant for storing values that are private, sensitive, or confidential in nature.
// For example: login username, login password, etc.
const useSecureStorage = () => {
  const { captureException } = useAppMonitor();
  // NOTE: size limit for a value is 2048 bytes; an attempt to store larger values may fail
  // See: https://docs.expo.dev/versions/latest/sdk/securestore/
  const saveSecureStringValue = useCallback(
    async ({ key, value }: { key: SecureStorageKey; value: string }) => {
      try {
        await setItemAsync(key, value);
      } catch (caughtErr) {
        captureException(caughtErr);
      }
    },
    [captureException],
  );

  const saveSecureBooleanValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/naming-convention
    async ({ key, value }: { key: SecureStorageKey; value: boolean }) => {
      try {
        await setItemAsync(key, value.toString());
      } catch (caughtErr) {
        captureException(caughtErr);
      }
    },
    [captureException],
  );

  const getSecureStringValue = useCallback(
    async (key: SecureStorageKey): Promise<null | string> => {
      try {
        return await getItemAsync(key);
      } catch (caughtErr) {
        captureException(caughtErr);
        return null;
      }
    },
    [captureException],
  );

  const getSecureBooleanValue = useCallback(
    async (key: SecureStorageKey): Promise<boolean> => {
      try {
        const value = await getItemAsync(key);
        return value === "true";
      } catch (caughtErr) {
        captureException(caughtErr);
        return false;
      }
    },
    [captureException],
  );

  const deleteSecureValue = useCallback(
    async (key: SecureStorageKey) => {
      try {
        await deleteItemAsync(key);
      } catch (caughtErr) {
        captureException(caughtErr);
      }
    },
    [captureException],
  );

  return {
    deleteSecureValue,
    getSecureBooleanValue,
    getSecureStringValue,
    saveSecureBooleanValue,
    saveSecureStringValue,
  };
};

export { SecureStorageKey, useSecureStorage };
